/**
 * ConnectionInputHandle - Reusable input handle component
 *
 * Renders the input (target) handle for both FlowScreenNode and FlowBlockNode.
 * Uses shared node-handle CSS classes and constants from handleConfig.
 *
 * Sprint: sprint-8
 * Pattern: Handle component reuse across entity types
 */

import React from 'react';
import { Handle, Position } from '@xyflow/react';
import { useTheme } from '@mui/material/styles';
import {
  HANDLE_DIMENSIONS,
  HANDLE_OFFSETS,
  HANDLE_TEXT,
  HANDLE_BORDER_RADIUS,
} from '@modules/reactFlow/constants/handleConfig';

export interface ConnectionInputHandleProps {
  entityId: string;
  isLaunchScreen?: boolean;
}

/**
 * ConnectionInputHandle - Input handle for entity nodes
 *
 * Renders the left-side input handle with:
 * - Theme-based styling
 * - CSS classes for hover effects
 * - Data attributes for testing
 * - Conditional rendering (hidden for launch screens)
 */
export const ConnectionInputHandle: React.FC<ConnectionInputHandleProps> = ({
  entityId,
  isLaunchScreen = false,
}) => {
  const theme = useTheme();

  // Launch screens don't have input handles
  if (isLaunchScreen) {
    return null;
  }

  return (
    <Handle
      type="target"
      position={Position.Left}
      id="input"
      className="node-custom-handle node-custom-handle-target"
      data-testid={`handle-input-${entityId}`}
      data-handle-type="target"
      data-handle-position="left"
      data-handlepos="left"
      style={{
        left: `${HANDLE_OFFSETS.INPUT_LEFT}px`,
        width: `${HANDLE_DIMENSIONS.INPUT_WIDTH}px`,
        height: `${HANDLE_DIMENSIONS.HEIGHT}px`,
        borderRadius: `${HANDLE_BORDER_RADIUS}px`,
        background: theme.palette.background.default,
        border: `1px solid ${theme.palette.divider}`,
        color: theme.palette.text.primary,
      }}
    >
      <span style={{ fontSize: HANDLE_TEXT.INPUT_FONT_SIZE, lineHeight: '1' }}>
        {HANDLE_TEXT.INPUT_LABEL}
      </span>
    </Handle>
  );
};
